import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, User, ChevronDown, Menu, X, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NavLink } from "react-router-dom";
import {
  LayoutGrid,
  Hammer,
  CalendarRange,
  BookOpen,
  Settings,
} from "lucide-react";

const MOBILE_NAV_ITEMS = [
  { to: "/", label: "Overview", icon: LayoutGrid, end: true, testid: "mobile-nav-home" },
  { to: "/forge", label: "App Forge", icon: Hammer, testid: "mobile-nav-app-forge" },
  { to: "/calendar", label: "Calendar · Maps", icon: CalendarRange, testid: "mobile-nav-calendar-maps" },
  { to: "/quran", label: "Quran Connections", icon: BookOpen, testid: "mobile-nav-quran-connections" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "mobile-nav-settings" },
];

export default function TopBar({ title, subtitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const initials = (user?.name || user?.email || "?")
    .split(/\s+|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header
      data-testid="top-bar"
      className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200"
    >
      <div className="flex items-center justify-between gap-4 px-4 sm:px-6 lg:px-10 py-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            className="md:hidden p-2 rounded-md hover:bg-slate-100 transition-colors"
            onClick={() => setMobileOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          <div className="md:hidden flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-slate-900 flex items-center justify-center">
              <Sparkles className="h-3.5 w-3.5 text-white" />
            </div>
            <span className="font-display font-bold text-slate-900">FullEcosystem</span>
          </div>

          <div className="hidden md:block min-w-0">
            {subtitle && (
              <div
                data-testid="top-bar-subtitle"
                className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500"
              >
                {subtitle}
              </div>
            )}
            <h1
              data-testid="top-bar-title"
              className="font-display text-xl font-semibold tracking-tight text-slate-900 truncate"
            >
              {title}
            </h1>
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-trigger"
              className="flex items-center gap-2 rounded-md hover:bg-slate-100 px-2 py-1.5 transition-colors"
            >
              <div className="h-8 w-8 rounded-md bg-slate-900 text-white flex items-center justify-center font-mono text-xs font-semibold">
                {initials || "U"}
              </div>
              <div className="hidden sm:block text-left leading-tight">
                <div className="text-sm font-medium text-slate-900 truncate max-w-[140px]">
                  {user?.name || "User"}
                </div>
                <div className="text-[11px] text-slate-500 truncate max-w-[140px]">
                  {user?.email}
                </div>
              </div>
              <ChevronDown className="h-4 w-4 text-slate-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="font-medium">{user?.name}</div>
              <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings" data-testid="user-menu-settings" className="cursor-pointer">
                <User className="mr-2 h-4 w-4" />
                <span>Profile & Settings</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="user-menu-logout"
              onClick={handleLogout}
              className="cursor-pointer text-red-600 focus:text-red-700"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {mobileOpen && (
        <div
          className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1"
          data-testid="mobile-nav"
        >
          {MOBILE_NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm",
                    isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100",
                  ].join(" ")
                }
              >
                <Icon className="h-4 w-4" strokeWidth={1.75} />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </header>
  );
}
